/**
 * offlineDB.js — IndexedDB stores for the offline-first data layer.
 *
 * Extends the existing 'gd-offline' database (mutation queue is DB v1).
 * This module upgrades to v2 adding:
 *   - apiCache:  stores GET response payloads keyed by URL
 *   - authCache: stores the last successful auth token/user pair
 *
 * The mutation queue store (v1) is left completely untouched.
 */

const DB_NAME    = 'gd-offline';
const DB_VERSION = 2;          // was 1 (mutation queue only)
const CACHE_TTL  = 7 * 24 * 60 * 60 * 1000;   // 7 days for API responses
const AUTH_TTL   = 30 * 24 * 60 * 60 * 1000;  // 30 days for auth cache

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      // v1 store — keep existing mutation queue unchanged
      if (!db.objectStoreNames.contains('queue')) {
        const store = db.createObjectStore('queue', { keyPath: 'id', autoIncrement: true });
        store.createIndex('by_status', 'status');
      }

      // v2 store — API response cache
      if (!db.objectStoreNames.contains('apiCache')) {
        const cache = db.createObjectStore('apiCache', { keyPath: 'url' });
        cache.createIndex('by_timestamp', 'timestamp');
      }

      // v2 store — auth cache (single record keyed 'current')
      if (!db.objectStoreNames.contains('authCache')) {
        db.createObjectStore('authCache', { keyPath: 'key' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

// ── API Cache ─────────────────────────────────────────────────────────────────

export async function cacheApiResponse(url, data) {
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx  = db.transaction('apiCache', 'readwrite');
      const req = tx.objectStore('apiCache').put({
        url,
        data,
        timestamp:  Date.now(),
        expiresAt:  Date.now() + CACHE_TTL,
      });
      req.onsuccess = resolve;
      req.onerror   = () => reject(req.error);
    });
  } catch { /* non-blocking */ }
}

export async function getCachedApiResponse(url) {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx  = db.transaction('apiCache', 'readonly');
      const req = tx.objectStore('apiCache').get(url);
      req.onsuccess = () => {
        const record = req.result;
        if (!record) return resolve(null);
        if (Date.now() > record.expiresAt) return resolve(null); // expired
        resolve(record.data);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function clearExpiredCache() {
  try {
    const db = await openDB();
    const now = Date.now();
    const tx  = db.transaction('apiCache', 'readwrite');
    const req = tx.objectStore('apiCache').openCursor();
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (!cursor) return;
      if (cursor.value.expiresAt < now) cursor.delete();
      cursor.continue();
    };
  } catch { /* non-blocking */ }
}

// ── Auth Cache ────────────────────────────────────────────────────────────────

export async function cacheAuth(token, user) {
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx  = db.transaction('authCache', 'readwrite');
      const req = tx.objectStore('authCache').put({ key: 'current', token, user, cachedAt: Date.now() });
      req.onsuccess = resolve;
      req.onerror   = () => reject(req.error);
    });
  } catch { /* non-blocking */ }
}

export async function getCachedAuth() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx  = db.transaction('authCache', 'readonly');
      const req = tx.objectStore('authCache').get('current');
      req.onsuccess = () => {
        const record = req.result;
        if (!record) return resolve(null);
        if (Date.now() - record.cachedAt > AUTH_TTL) return resolve(null); // expired
        resolve({ token: record.token, user: record.user });
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function clearCachedAuth() {
  try {
    const db = await openDB();
    await new Promise((resolve, reject) => {
      const tx  = db.transaction('authCache', 'readwrite');
      const req = tx.objectStore('authCache').delete('current');
      req.onsuccess = resolve;
      req.onerror   = () => reject(req.error);
    });
  } catch { /* non-blocking */ }
}
